import { useMemo, useState } from 'react';
import type { LstCargo, LstExecucao, LstFrequency, LstStatus, LstTemplate } from '../types';
import { todayISO } from '../services/lstService';
import {
  buildHourlyGrid,
  buildShiftHourSlots,
  formatTimeHHmm,
  getHourlyTemplates,
  hourBlockHasUpcomingAlert,
  type HourCellData,
} from '../services/lstMatrixHourly';
import { isTemplateDueOnDate } from '../services/lstSchedule';
import { templateAppliesToUser } from '../utils/lstTemplateScope';
import { isLstSlotStillOpen } from '../utils/lstEffectiveStatus';

export type KamishibaiMatrixViewMode = 'activities' | 'hourly';

type LstBlock = 'diaria' | 'semanal' | 'mensal';

const BLOCK_META: Record<LstBlock, { label: string; prefix: string }> = {
  diaria: { label: 'Frequência Diária', prefix: 'D' },
  semanal: { label: 'Frequência Semanal', prefix: 'S' },
  mensal: { label: 'Frequência Mensal', prefix: 'M' },
};

const BLOCK_ORDER: LstBlock[] = ['diaria', 'semanal', 'mensal'];

function blockOf(freq: LstFrequency): LstBlock {
  if (freq === 'semanal') return 'semanal';
  if (freq === 'mensal') return 'mensal';
  return 'diaria';
}

function nowMinutes(): number {
  const n = new Date();
  return n.getHours() * 60 + n.getMinutes();
}

function cellState(
  block: LstBlock,
  stillOpen: boolean,
  exec: LstExecucao | null,
): 'ok' | 'desvio' | 'perdido' | 'futuro' | 'na' {
  if (exec) {
    if (exec.status === 'realizado' || exec.status === 'processo_parado') return 'ok';
    if (exec.status === 'realizado_com_desvio') return 'desvio';
    if (exec.status === 'nao_realizado') return 'perdido';
  }
  if (stillOpen) return 'futuro';
  return block === 'diaria' ? 'perdido' : 'na';
}

export interface KamishibaiMatrixStats {
  ok: number;
  desvio: number;
  perdido: number;
  pct: number;
  alertBlocks?: number;
}

export interface UseKamishibaiMatrixModelOptions {
  cargo: LstCargo;
  templates: LstTemplate[];
  execucoes: LstExecucao[];
  monthDates: string[];
  userId: string;
}

export function useKamishibaiMatrixModel({
  cargo,
  templates,
  execucoes,
  monthDates,
  userId,
}: UseKamishibaiMatrixModelOptions) {
  const today = todayISO();
  const [viewMode, setViewMode] = useState<KamishibaiMatrixViewMode>('activities');

  const execIndex = useMemo(() => {
    const map = new Map<string, LstExecucao>();
    for (const e of execucoes) {
      if (e.user_id === userId) map.set(`${e.template_id}__${e.data}`, e);
    }
    return map;
  }, [execucoes, userId]);

  const userTemplates = useMemo(
    () => templates.filter((t) => templateAppliesToUser(t, cargo, userId)),
    [templates, cargo, userId],
  );

  const blocks = useMemo(() => {
    const byBlock: Record<LstBlock, LstTemplate[]> = { diaria: [], semanal: [], mensal: [] };
    for (const t of userTemplates) {
      byBlock[blockOf(t.frequency)].push(t);
    }
    const sortFn = (a: LstTemplate, b: LstTemplate) => {
      const ta = formatTimeHHmm(a.scheduled_time) ?? '';
      const tb = formatTimeHHmm(b.scheduled_time) ?? '';
      if (ta && tb && ta !== tb) return ta < tb ? -1 : 1;
      return a.sort_order - b.sort_order;
    };
    return BLOCK_ORDER.map((block) => ({
      block,
      meta: BLOCK_META[block],
      items: byBlock[block].sort(sortFn).map((template, i) => ({
        template,
        code: `${BLOCK_META[block].prefix}${i + 1}`,
      })),
    })).filter((b) => b.items.length > 0);
  }, [userTemplates]);

  const days = useMemo(() => {
    const WEEKDAY_SHORT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
    return monthDates.map((iso) => {
      const [y, m, d] = iso.split('-').map(Number);
      const dow = new Date(y, m - 1, d).getDay();
      return {
        iso,
        num: d,
        weekday: WEEKDAY_SHORT[dow],
        isWeekend: dow === 0 || dow === 6,
      };
    });
  }, [monthDates]);

  const hourSlots = useMemo(() => buildShiftHourSlots(userTemplates, cargo), [userTemplates, cargo]);
  const hourlyGrid = useMemo(
    () => buildHourlyGrid(userTemplates, cargo, monthDates, execucoes, userId, today),
    [userTemplates, cargo, monthDates, execucoes, userId, today],
  );

  const activityStats = useMemo((): KamishibaiMatrixStats => {
    let ok = 0;
    let desvio = 0;
    let perdido = 0;
    for (const grp of blocks) {
      for (const { template } of grp.items) {
        for (const day of days) {
          if (!isTemplateDueOnDate(template, day.iso, monthDates)) continue;
          const exec = execIndex.get(`${template.id}__${day.iso}`) ?? null;
          const st = cellState(
            grp.block,
            isLstSlotStillOpen(template.scheduled_time, day.iso, today),
            exec,
          );
          if (st === 'ok') ok += 1;
          else if (st === 'desvio') desvio += 1;
          else if (st === 'perdido') perdido += 1;
        }
      }
    }
    const base = ok + desvio + perdido;
    const pct = base === 0 ? 0 : Math.round(((ok + desvio) / base) * 100);
    return { ok, desvio, perdido, pct };
  }, [blocks, days, execIndex, today, monthDates]);

  const hourlyStats = useMemo((): KamishibaiMatrixStats => {
    let ok = 0;
    let desvio = 0;
    let perdido = 0;
    let alertBlocks = 0;
    const nowMin = nowMinutes();
    for (const cell of hourlyGrid.values()) {
      for (const a of cell.activities) {
        if (a.cardState === 'ok') ok += 1;
        else if (a.cardState === 'desvio') desvio += 1;
        else if (a.cardState === 'perdido') perdido += 1;
      }
      if (hourBlockHasUpcomingAlert(cell, today, nowMin)) alertBlocks += 1;
    }
    const base = ok + desvio + perdido;
    const pct = base === 0 ? 0 : Math.round(((ok + desvio) / base) * 100);
    return { ok, desvio, perdido, pct, alertBlocks };
  }, [hourlyGrid, today]);

  const totalRotinas = blocks.reduce((acc, b) => acc + b.items.length, 0);
  const hasHourlyData = getHourlyTemplates(userTemplates, cargo).length > 0;

  return {
    today,
    viewMode,
    setViewMode,
    blocks,
    days,
    execIndex,
    hourSlots,
    hourlyGrid,
    activityStats,
    hourlyStats,
    totalRotinas,
    hasHourlyData,
    monthDates,
  };
}

export type KamishibaiMatrixModel = ReturnType<typeof useKamishibaiMatrixModel>;

export type { HourCellData, LstBlock };
